import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ExamRunner() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    api.get(`/exams/${examId}`).then((r) => {
      setExam(r.data);
      if (r.data.submitted) setResult({ score: r.data.score, correct: r.data.correct, total: r.data.total, detail: r.data.detail, subject_results: r.data.subject_results });
    });
  }, [examId]);

  if (!exam) return <div className="text-slate-500">Yükleniyor…</div>;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/exams/${examId}/submit`, {
        answers, duration_seconds: Math.round((Date.now() - startTime) / 1000),
      });
      setResult(r.data);
    } catch {
      toast.error("Gönderilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <ExamResult exam={exam} result={result} />;

  const q = exam.questions[idx];
  const total = exam.questions.length;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="space-y-4 ela-fade-in" data-testid="exam-runner">
      <Link to="/denemeler" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Deneme Merkezi
      </Link>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900">{exam.type === "lgs" ? "LGS Denemesi" : "Mini Deneme"}</h1>
          <p className="text-xs text-slate-500">{q.ders} · {q.konu}</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Soru</div>
          <div className="font-display text-lg font-bold">{idx + 1}/{total}</div>
        </div>
      </div>
      <Progress value={((idx + 1) / total) * 100} className="h-2" />

      <Card className="p-5 rounded-2xl border-slate-200">
        <div className="text-sm text-slate-900 whitespace-pre-wrap">{q.soru}</div>
        <div className="mt-4 grid sm:grid-cols-2 gap-2">
          {Object.entries(q.secenekler || {}).map(([k, v]) => {
            const selected = answers[idx] === k;
            return (
              <button key={k} type="button"
                data-testid={`exam-q${idx}-opt-${k}`}
                onClick={() => setAnswers({ ...answers, [idx]: k })}
                className={`text-left p-3 rounded-xl border text-sm ${
                  selected ? "border-[#FF6B6B] bg-rose-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                <span className="font-semibold mr-2">{k})</span>{v}
              </button>
            );
          })}
        </div>
      </Card>

      <div className="flex gap-2">
        <Button variant="outline" disabled={idx === 0}
          data-testid="exam-prev-btn"
          onClick={() => setIdx(idx - 1)} className="rounded-xl">Önceki</Button>
        {idx < total - 1 ? (
          <Button
            data-testid="exam-next-btn"
            onClick={() => setIdx(idx + 1)}
            className="rounded-xl bg-[#FF6B6B] hover:bg-[#FF5252] flex-1">Sonraki</Button>
        ) : (
          <Button
            data-testid="exam-finish-btn"
            onClick={submit} disabled={submitting || answeredCount < total}
            className="rounded-xl bg-slate-900 hover:bg-slate-800 flex-1">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Gönderiliyor</> : `Bitir (${answeredCount}/${total})`}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-10 gap-1.5">
        {exam.questions.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)}
            data-testid={`exam-jump-${i}`}
            className={`h-7 rounded-md text-[11px] font-medium ${
              i === idx ? "bg-slate-900 text-white" :
              answers[i] ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500"
            }`}>{i+1}</button>
        ))}
      </div>
    </div>
  );
}

function ExamResult({ exam, result }) {
  return (
    <div className="space-y-4 ela-fade-in" data-testid="exam-result">
      <Link to="/denemeler" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Deneme Merkezi
      </Link>
      <Card className="p-6 rounded-2xl border-slate-200 text-center bg-rose-50/30">
        <div className="text-xs text-slate-500 uppercase tracking-wider">{exam.type === "lgs" ? "LGS Denemesi" : "Mini Deneme"}</div>
        <div className="font-display text-6xl font-bold text-slate-900 mt-2">{result.score}</div>
        <div className="text-sm text-slate-500">/ 100 · {result.correct}/{result.total} doğru</div>
      </Card>

      {result.subject_results?.length > 0 && (
        <Card className="p-5 rounded-2xl border-slate-200">
          <h3 className="font-heading font-semibold mb-3">Ders Bazında</h3>
          <div className="space-y-2">
            {result.subject_results.map((s) => (
              <div key={s.ders} className="flex items-center gap-3">
                <span className="text-sm w-24">{s.ders}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#FF6B6B]" style={{ width: `${s.accuracy}%` }} />
                </div>
                <span className="text-xs font-display w-16 text-right">{s.correct}/{s.total} (%{s.accuracy})</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-heading font-semibold">Soru Detayları</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {result.detail.map((d, i) => (
            <div key={i} className="p-4">
              <div className="flex items-start gap-2">
                {d.dogru_mu ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" /> :
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5" />}
                <div className="flex-1">
                  <div className="text-xs text-slate-500">{d.ders} · Soru {i+1}</div>
                  <div className="text-sm text-slate-700">
                    Seçtiğin: <span className="font-semibold">{d.chosen || "—"}</span> · Doğru: <span className="font-semibold text-green-700">{d.dogru}</span>
                  </div>
                  {d.aciklama && <div className="text-xs text-slate-600 mt-1">{d.aciklama}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
